// src/modules/app-views/serviceOrders/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import ServiceOrdersInit from './pages/Init';
import ListProvider from 'components/core/ListProvider';
import { API } from 'constants/Api';
import serviceOrdersInitActionButton from 'modules/app-views/serviceOrders/pages/Init/actions/actionButton';
import serviceOrdersInitMenuButton from 'modules/app-views/serviceOrders/pages/Init/actions/menuButton';
import DocumentProvider from 'components/core/DocumentProvider';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import CardHeaderSubtitle from 'layouts/Pages/PageHeader/CardHeaderSubtitle';
import ServiceOrdersDocument from 'modules/app-views/serviceOrders/pages/Document';
import serviceOrdersDocumentActionButton from 'modules/app-views/serviceOrders/pages/Document/actions/actionButton';
import serviceOrdersDocumentMenuButton from 'modules/app-views/serviceOrders/pages/Document/actions/menuButton';
import serviceOrdersDocumentDecorateRequest from './pages/Document/decorators/request';
import CardHeaderIcons from 'layouts/Pages/PageHeader/CardHeaderIcons';
import ServiceOrdersFilterDrawerBody from './components/FilterDrawerBody';
import ServiceOrdersCardHeaderColumn1 from 'modules/app-views/serviceOrders/pages/Document/components/CardHeaderColumn1';
import ServiceOrdersCardHeaderColumn2 from 'modules/app-views/serviceOrders/pages/Document/components/CardHeaderColumn2';
import ServiceOrdersCardHeaderColumn3 from 'modules/app-views/serviceOrders/pages/Document/components/CardHeaderColumn3';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'serviceOrders';

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 1,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: null,
  parametersSeed: null,

  // ───────────────────────────────────────────────────────────────────────
  // MIGRATORY
  // ───────────────────────────────────────────────────────────────────────
  migratory: {
    title: null,
    schema: [],
  },
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'list',
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <ServiceOrdersInit />
      </ListProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.SERVICE_ORDERS.PAGINATION,
      params: null,
      query: null,

      returnAsObject: false,

      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      responseDoc: null,
      responseData: null,
    },


    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: null,
      paramKey: null,
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [
        { field: '_metadata.audit.updatedAt', order: 'desc', index: 1 },
      ],
      search: {},
      textSearch: '',
      filter: {},
      drawer: { folder: 'inbox' },
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: {
      actions: ['edit', 'archive', 'remove'],
      hiddenColumns: ['line', 'documentCode', 'createdAt'],
    },

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: null,

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'default',
      actions: ['filter', 'refresh', serviceOrdersInitActionButton, serviceOrdersInitMenuButton],

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // FILTER DRAWER
    // ───────────────────────────────────────────────────────────────────────
    filterDrawer: {
      components: {
        body: props => <ServiceOrdersFilterDrawerBody {...props} />,
      },
    },
  },

  document: {
    highSecurity: false,

    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <ServiceOrdersDocument />
      </DocumentProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.SERVICE_ORDERS.URL,
      params: null,
      query: null,

      returnAsObject: true,

      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: serviceOrdersDocumentDecorateRequest,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/:_id',
      paramKey: '_id',
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: null,

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: null,

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      initialTab: 'record',
      initialValues: {
        type: 'budget',
        status: 'draft',
        items: [],
        priority: 'normal',
      },
      actions: [
        'editRefresh',
        serviceOrdersDocumentActionButton(),
        serviceOrdersDocumentMenuButton(),
        'return',
      ],
      viewMode: 'auto',
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'card',

      avatar: {
        visible: true,
        placeholder: 'tool',
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },

      card: {
        title: props => <CardHeaderTitle {...props} name={props?.name} />,
        subtitle: props => <CardHeaderSubtitle {...props} fieldName="documentNumber" />,

        column1: props => <ServiceOrdersCardHeaderColumn1 {...props} />,
        column2: props => <ServiceOrdersCardHeaderColumn2 {...props} />,
        column3: props => <ServiceOrdersCardHeaderColumn3 {...props} />,

        icons: (data, countryCode) => <CardHeaderIcons data={data} countryCode={countryCode} />,
      },
    },
  },
};

// *******************************************************************************
// MOCK DATA
// *******************************************************************************
const MOCK = {
  document: {},
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, MOCK, preConfigChanges };
